/**
 * ZicassoLeadModal — Modal de detalle con tabs: Details · Messages · Documents ·
 * Sales · Follow-up · Best Practices. Composer con templates + sugerencia.
 */
import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  RefreshCw, AlertTriangle, Calendar, MapPin, DollarSign, Users, Phone,
  Send, Mail, FileText, Award, ListChecks, Sparkles, Clock, User, Copy,
  BookOpen, MessageSquare, MessageSquareText, Check, X, Lightbulb, ScrollText, Bell, Trash2, Plus,
} from "lucide-react";

import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { STAGES } from "@/lib/zicassoConstants";
import { sanitizeHtml, htmlToText } from "@/lib/html";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { LogActivityDialog } from "@/components/LogActivityDialog";
import { RichTextEditor } from "@/components/RichTextEditor";
import { SendSmsDialog } from "@/components/SendSmsDialog";
import { FEATURES } from "@/lib/features";

const stageMeta = (key) => STAGES.find((s) => s.key === (key || "")) || STAGES[0];
const initials = (n = "") => n.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
const relativeDate = (iso) => {
  if (!iso || iso.startsWith("0001")) return "—";
  try { return format(new Date(iso), "d MMM yyyy", { locale: es }); } catch { return "—"; }
};

const dateTime = (iso) => {
  if (!iso || iso.startsWith("0001")) return "—";
  try { return format(new Date(iso), "d MMM yyyy · HH:mm", { locale: es }); } catch { return "—"; }
};

const MSG_TYPE_LABEL = {
  PHONE_CALL_UNANSWERED: "📞 Llamada sin respuesta",
  PHONE_CALL_ANSWERED: "📞 Llamada",
  PHONE_CALL: "📞 Llamada",
  VOICEMAIL: "📞 Voicemail",
  WHATSAPP: "WhatsApp",
};

const PRIORITY_COLORS = {
  high: "border-[#C45A45]/30 bg-[#FCEEEA] text-[#803D26]",
  medium: "border-[#C28F5B]/30 bg-[#F1E9DA] text-[#6B4F20]",
  low: "border-black/10 bg-secondary text-foreground",
};

function InfoBadge({ icon: Icon, label, value }) {
  return (
    <div className="flex flex-col rounded-lg border border-black/5 bg-card p-3">
      <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3 w-3" />{label}
      </div>
      <div className="truncate text-sm font-medium">{value || "—"}</div>
    </div>
  );
}

function DetailsTab({ lead }) {
  const traveler = (lead.raw_trip_match?.travelers || [])[0] || {};
  const rawPrefix = (lead.traveler_phone_prefix || "").toString();
  const prefix = rawPrefix.startsWith("+") ? rawPrefix : rawPrefix ? `+${rawPrefix}` : "";
  const phone = `${prefix} ${lead.traveler_phone_number || ""}`.trim();
  const description = lead.client_brief || lead.cached_trip_request?.tripDescription || lead.cached_trip_request?.description || "";
  const emails = (traveler.userInfo || []).map((u) => u.email).filter(Boolean);
  const primaryEmail = lead.traveler_email || emails[0] || "";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <InfoBadge icon={MapPin} label="Destino" value={(lead.countries || []).join(", ")} />
        <InfoBadge icon={Calendar} label="Salida" value={relativeDate(lead.departure_date)} />
        <InfoBadge icon={Calendar} label="Vuelta" value={relativeDate(lead.return_date)} />
        <InfoBadge icon={Users} label="Pax" value={`${lead.adults}A ${lead.children ? `${lead.children}N` : ""} · ${lead.total_nights}n`} />
        <InfoBadge icon={DollarSign} label="Budget" value={lead.budget_usd ? `$${(lead.budget_usd || 0).toLocaleString()}` : "—"} />
        <InfoBadge icon={Phone} label="Teléfono" value={phone || "—"} />
        <InfoBadge icon={Mail} label="Email" value={primaryEmail || "—"} />
        <InfoBadge icon={Award} label="Repeat" value={lead.repeat_traveler ? "Cliente recurrente" : "No"} />
        <InfoBadge icon={User} label="Residencia" value={lead.traveler_country || "—"} />
      </div>

      {description && (
        <Card>
          <CardContent className="p-4">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4" /> Brief del cliente (Zicasso)
            </h3>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{description}</p>
          </CardContent>
        </Card>
      )}

      <SofiNoteEditor lead={lead} />
    </div>
  );
}

function SofiNoteEditor({ lead }) {
  const qc = useQueryClient();
  const [text, setText] = useState(lead.sofi_notes || "");
  const mut = useMutation({
    mutationFn: (t) => api.post(`/zicasso/leads/${lead.id}/note`, { text: t }),
    onSuccess: () => {
      toast.success("Nota guardada");
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
    },
  });
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-2 text-sm font-semibold">Nota interna (privada)</h3>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Nota que solo se ve aquí (no se envía a Zicasso)…"
          rows={3}
          data-testid="sofi-note-textarea"
        />
        <Button size="sm" className="mt-2" onClick={() => mut.mutate(text)} disabled={mut.isPending} data-testid="save-sofi-note-button">
          <Check className="mr-1 h-3 w-3" /> Guardar nota
        </Button>
      </CardContent>
    </Card>
  );
}

function MessagesTab({ lead }) {
  const qc = useQueryClient();
  const editorRef = useRef(null);
  const [body, setBody] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const [draftInfo, setDraftInfo] = useState("");
  const [aiInstructions, setAiInstructions] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);

  const templates = useQuery({
    queryKey: ["zicasso-templates"],
    queryFn: async () => (await api.get("/zicasso/templates")).data,
    staleTime: 5 * 60_000,
  });

  const sendMut = useMutation({
    mutationFn: () => api.post(`/zicasso/leads/${lead.id}/send-message`, { body, body_html: bodyHtml || null, subject: subject || null }),
    onSuccess: (res) => {
      toast.success(res.data?.demo
        ? "Mensaje registrado (modo demo)"
        : `Mensaje enviado a Zicasso como ${res.data?.sent_as || lead.assigned_zc_agent_name || "el agente asignado"}`);
      editorRef.current?.clear(); setBodyHtml(""); setSubject("");
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error enviando mensaje"),
  });

  const refreshMut = useMutation({
    mutationFn: () => api.get(`/zicasso/leads/${lead.id}/messages?refresh=true`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
      toast.success("Conversación actualizada");
    },
  });

  const renderMut = useMutation({
    mutationFn: (templateId) => api.post(`/zicasso/leads/${lead.id}/render-template`, { template_id: templateId }),
    onSuccess: (r) => {
      editorRef.current?.setText(r.data.text);
      setShowTemplates(false);
      toast.success("Plantilla personalizada aplicada");
    },
    onError: () => toast.error("No se pudo personalizar la plantilla"),
  });

  const composeMut = useMutation({
    mutationFn: () => api.get(`/zicasso/leads/${lead.id}/compose-followup`),
    onSuccess: (r) => {
      editorRef.current?.setText(r.data.text);
      if (r.data.subject) setSubject(r.data.subject);
      setDraftInfo(r.data.used_templates?.length ? `Plantilla · ${r.data.used_templates.join(" + ")}` : "Plantilla del paso actual");
      toast.success(r.data.used_templates?.length ? `Plantilla aplicada (${r.data.used_templates.join(" + ")})` : "Plantilla del paso aplicada");
    },
    onError: () => toast.error("No se pudo generar el borrador"),
  });

  const aiMut = useMutation({
    mutationFn: () => api.post(`/zicasso/leads/${lead.id}/ai-draft`, { instructions: aiInstructions || null }),
    onSuccess: (r) => {
      editorRef.current?.setText(r.data.text || "");
      if (r.data.subject) setSubject(r.data.subject);
      if (r.data.source === "template_fallback") {
        setDraftInfo(`Plantilla (IA no disponible) · ${r.data.used_template || ""}`);
        toast.warning(r.data.warning || "IA no disponible; se usó plantilla");
      } else {
        setDraftInfo(r.data.used_template ? `IA · basada en ${r.data.used_template}` : "Generado con IA");
        toast.success("Borrador generado con IA");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo generar el borrador con IA"),
  });

  const applyTemplate = (tpl) => renderMut.mutate(tpl.id);

  return (
    <div className="flex h-[560px] flex-col gap-3">
      <div className="thin-scroll flex-1 overflow-y-auto rounded-lg border bg-secondary/40 p-3" data-testid="messages-history">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Historial ({lead.messages_count || 0})
          </span>
          <Button size="sm" variant="ghost" onClick={() => refreshMut.mutate()} disabled={refreshMut.isPending}>
            <RefreshCw className={`h-3 w-3 ${refreshMut.isPending ? "animate-spin" : ""}`} />
          </Button>
        </div>
        {(lead.messages || []).length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Sin mensajes intercambiados aún.</p>
        ) : (
          <div className="space-y-2">
            {(lead.messages || []).map((m) => {
              const fromPartner = (m.sender_type || "").toUpperCase() === "PARTNER";
              return (
                <div
                  key={m.id}
                  className={`rounded-lg border p-3 text-sm ${fromPartner ? "border-primary/10 bg-primary/5" : "border-black/5 bg-card"}`}
                  data-testid={`message-${m.id}`}
                >
                  <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="font-semibold">
                      {m.sender_name || m.sender_type || "—"}
                      {m.message_type && m.message_type !== "TEXT" && (
                        <span className="ml-1.5 rounded bg-[#E5E8F2] px-1 py-0.5 text-[9px] font-medium text-[#34406B]">
                          {MSG_TYPE_LABEL[m.message_type] || "Registro"}
                        </span>
                      )}
                    </span>
                    <span>{dateTime(m.sent_at)}</span>
                  </div>
                  {m.subject && <div className="mb-1 text-xs font-semibold">{m.subject}</div>}
                  <div
                    className="prose prose-sm max-w-none text-xs leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_p]:my-1"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.body || "") }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-2 rounded-lg border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Nuevo mensaje</h3>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => aiMut.mutate()} disabled={aiMut.isPending} data-testid="ai-draft-button">
              <Sparkles className={`mr-1 h-3 w-3 ${aiMut.isPending ? "animate-pulse" : ""}`} />
              {aiMut.isPending ? "Generando…" : "Generar con IA"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => composeMut.mutate()} disabled={composeMut.isPending} data-testid="compose-followup-button">
              <FileText className="mr-1 h-3 w-3" />
              {composeMut.isPending ? "…" : "Usar plantilla"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowTemplates(!showTemplates)} data-testid="toggle-templates-button">
              <BookOpen className="mr-1 h-3 w-3" />
              Templates ({templates.data?.length || 0})
            </Button>
          </div>
        </div>
        {draftInfo && (
          <div className="flex items-center gap-1 text-[10px] font-medium text-primary" data-testid="draft-source-info">
            <Sparkles className="h-3 w-3" /> {draftInfo}
          </div>
        )}
        <Input
          value={aiInstructions}
          onChange={(e) => setAiInstructions(e.target.value)}
          placeholder='Instrucción para la IA (opcional): "ofrece una llamada", "menciona el presupuesto"…'
          className="text-xs"
          data-testid="ai-instructions-input"
        />
        {showTemplates && (
          <div className="thin-scroll max-h-[180px] space-y-1 overflow-y-auto rounded-lg border bg-secondary/40 p-2" data-testid="templates-list">
            {(templates.data || []).map((t) => (
              <button
                key={t.id}
                onClick={() => applyTemplate(t)}
                className="flex w-full items-start justify-between rounded-md p-2 text-left text-xs hover:bg-accent"
                data-testid={`template-${t.id}`}
              >
                <div>
                  <div className="font-semibold">{t.title}</div>
                  <div className="mt-0.5 line-clamp-2 text-[10px] text-muted-foreground">
                    {htmlToText(t.body).substring(0, 100)}…
                  </div>
                </div>
                <Copy className="ml-2 h-3 w-3 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (opcional)" data-testid="message-subject-input" />
        <RichTextEditor
          ref={editorRef}
          placeholder="Escribe el mensaje aquí… (usa la barra para negrita, enlaces, listas)"
          testid="message-body-editor"
          minHeight={130}
          onChange={({ html, text }) => { setBody(text); setBodyHtml(html); }}
        />
        {FEATURES.whatsapp && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#25D366]/30 bg-[#25D366]/5 p-2.5" data-testid="whatsapp-block">
            <span className="flex items-center gap-1.5 text-[11px] text-[#128C4B]">
              <MessageSquare className="h-3.5 w-3.5" /> Enviar por WhatsApp desde el número de empresa
            </span>
            <WhatsAppButton lead={lead} text={body} onLogged={() => qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] })} />
          </div>
        )}
        {FEATURES.sms && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/25 bg-primary/5 p-2.5" data-testid="sms-block">
            <span className="flex items-center gap-1.5 text-[11px] text-primary">
              <MessageSquareText className="h-3.5 w-3.5" /> Envía un SMS al móvil del viajero (desde tu número Zicasso)
            </span>
            <Button size="sm" onClick={() => setSmsOpen(true)} data-testid="open-sms-button">
              <MessageSquareText className="mr-1.5 h-3.5 w-3.5" /> Enviar SMS
            </Button>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-2.5" data-testid="log-call-block">
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Phone className="h-3.5 w-3.5" /> Registra una llamada o SMS (interno, se guarda en Zicasso)
          </span>
          <Button size="sm" variant="outline" onClick={() => setLogOpen(true)} data-testid="log-call-open-button">
            <Phone className="mr-1.5 h-3.5 w-3.5" /> Registrar llamada / SMS
          </Button>
        </div>
        <LogActivityDialog
          open={logOpen}
          onOpenChange={setLogOpen}
          lead={lead}
          defaultKind="answered"
          onLogged={() => qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] })}
        />
        <SendSmsDialog
          open={smsOpen}
          onOpenChange={setSmsOpen}
          lead={lead}
          onSent={() => qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] })}
        />
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="send-as-hint">
            <Send className="h-3 w-3" /> Se enviará como <span className="font-medium text-foreground">{lead.assigned_zc_agent_name || "agente asignado"}</span>
          </span>
          <div className="flex items-center gap-2">
            {body && (
              <Button size="sm" variant="ghost" onClick={() => { editorRef.current?.clear(); setBodyHtml(""); setSubject(""); }} data-testid="clear-composer-button">
                <X className="mr-1 h-3 w-3" /> Limpiar
              </Button>
            )}
            <Button onClick={() => sendMut.mutate()} disabled={sendMut.isPending || !body.trim()} data-testid="send-message-button">
              <Send className={`mr-2 h-3 w-3 ${sendMut.isPending ? "animate-pulse" : ""}`} />
              {sendMut.isPending ? "Enviando…" : "Enviar"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentsTab({ lead }) {
  const docs = lead.documents || [];
  return (
    <div>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <FileText className="h-4 w-4" /> Documentos ({docs.length})
      </h3>
      {docs.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          Sin documentos en Zicasso para este lead.
        </p>
      ) : (
        <div className="space-y-2">
          {docs.map((d, i) => (
            <Card key={d.id || i}>
              <CardContent className="flex items-center justify-between p-3">
                <div>
                  <div className="text-sm font-medium">{d.name || d.title || "Documento"}</div>
                  <div className="text-[11px] text-muted-foreground">{d.type || d.documentType} · {relativeDate(d.createdAt)}</div>
                </div>
                {d.url && (
                  <Button size="sm" variant="outline" asChild>
                    <a href={d.url} target="_blank" rel="noopener noreferrer">Abrir</a>
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SalesTab({ lead }) {
  const sr = lead.sales_recordings || [];
  const rv = lead.reviews || [];
  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <DollarSign className="h-4 w-4" /> Sales recordings ({sr.length})
        </h3>
        {sr.length === 0 ? (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            Sin sales recordings. Se generan cuando el trip pasa a SOLD.
          </p>
        ) : (
          <pre className="thin-scroll max-h-[280px] overflow-auto rounded-lg bg-secondary/40 p-2 text-[10px]">{JSON.stringify(sr, null, 2)}</pre>
        )}
      </div>
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <Award className="h-4 w-4" /> Reviews del traveler ({rv.length})
        </h3>
        {rv.length === 0 ? (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            Sin reviews. Se generan cuando el traveler vuelve tras el viaje.
          </p>
        ) : (
          <pre className="thin-scroll max-h-[200px] overflow-auto rounded-lg bg-secondary/40 p-2 text-[10px]">{JSON.stringify(rv, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}

function FollowUpTab({ lead }) {
  const qc = useQueryClient();
  const suggested = useQuery({
    queryKey: ["zicasso-suggested", lead.id],
    queryFn: async () => (await api.get(`/zicasso/leads/${lead.id}/suggested-next-action`)).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
    qc.invalidateQueries({ queryKey: ["zicasso-leads"] });
    qc.invalidateQueries({ queryKey: ["zicasso-stats"] });
  };

  const snoozeMut = useMutation({
    mutationFn: (days) => api.put(`/zicasso/leads/${lead.id}/snooze`, { days }),
    onSuccess: () => { toast.success("Reminder pospuesto"); invalidate(); },
  });
  const dismissMut = useMutation({
    mutationFn: () => api.put(`/zicasso/leads/${lead.id}/dismiss-reminder`, {}),
    onSuccess: () => { toast.success("Reminder marcado como hecho"); invalidate(); },
  });
  const inactivateMut = useMutation({
    mutationFn: () => api.put(`/zicasso/leads/${lead.id}/status`, { status: "INACTIVE" }),
    onSuccess: () => { toast.success("Lead marcado como INACTIVE"); invalidate(); },
  });

  const s = suggested.data;
  return (
    <div className="space-y-4" data-testid="followup-tab">
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-4 w-4" /> Timeline follow-up (Zicasso)
          </h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Step actual</div>
              <div className="font-heading text-lg font-semibold">
                {lead.reminder_index !== null && lead.reminder_index !== undefined && lead.reminder_index >= 0 ? `${lead.reminder_index} / 9` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Próxima acción</div>
              <div className="text-sm">{lead.timeline_step_label || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Due</div>
              <div className={`text-sm font-medium ${lead.is_overdue ? "text-[#C45A45]" : ""}`}>
                {relativeDate(lead.reminder_due_at)}{lead.is_overdue && <span className="ml-1">(atrasado)</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {suggested.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : s && (
        <Card className={`border-2 ${PRIORITY_COLORS[s.priority] || ""}`}>
          <CardContent className="p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="h-4 w-4" /> Sugerencia inteligente
              </h3>
              <Badge variant="outline" className={PRIORITY_COLORS[s.priority]}>Prioridad {s.priority}</Badge>
            </div>
            <p className="text-sm">{s.hint}</p>
            {s.suggested_template && (
              <div className="mt-3 rounded-lg border bg-card p-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Template recomendado</div>
                <div className="text-sm font-semibold">{s.suggested_template.title}</div>
                <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">
                  {htmlToText(s.suggested_template.body).substring(0, 200)}…
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 text-sm font-semibold">Acciones</h3>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => dismissMut.mutate()} disabled={dismissMut.isPending} data-testid="dismiss-reminder-button">
              <Check className="mr-1 h-3 w-3" /> Marcar hecho
            </Button>
            {[1, 3, 7].map((d) => (
              <Button key={d} size="sm" variant="outline" onClick={() => snoozeMut.mutate(d)} disabled={snoozeMut.isPending} data-testid={`snooze-${d}d-button`}>
                <Clock className="mr-1 h-3 w-3" /> Snooze {d}d
              </Button>
            ))}
            <div className="w-full pt-2" />
            <Button
              size="sm" variant="destructive"
              onClick={() => { if (window.confirm("¿Marcar este lead como INACTIVE en Zicasso?")) inactivateMut.mutate(); }}
              disabled={inactivateMut.isPending}
              data-testid="mark-inactive-button"
            >
              <X className="mr-1 h-3 w-3" /> Marcar INACTIVE
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BestPracticesTab({ lead }) {
  const bp = useQuery({
    queryKey: ["zicasso-best-practices"],
    queryFn: async () => (await api.get("/zicasso/best-practices")).data,
    staleTime: 30 * 60_000,
  });
  if (bp.isLoading || !bp.data) return <Skeleton className="h-96 w-full" />;
  const currentStep = lead.reminder_index;

  return (
    <div className="space-y-4" data-testid="best-practices-tab">
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <ListChecks className="h-4 w-4 text-primary" /> Timeline oficial de Zicasso
          </h3>
          <div className="space-y-2" data-testid="timeline-list">
            {bp.data.timeline.map((step) => {
              const isCurrent = currentStep === step.step;
              return (
                <div
                  key={step.step}
                  className={`flex gap-3 rounded-lg border p-3 ${isCurrent ? "ring-2 ring-primary" : ""} ${PRIORITY_COLORS[step.priority]}`}
                  data-testid={`timeline-step-${step.step}`}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-card text-sm font-bold shadow-sm">
                    {step.step}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{step.day}</span>
                      <Badge variant="outline" className="h-4 text-[9px]">{step.channel === "call" ? "Llamada" : "Mensaje"}</Badge>
                      {isCurrent && <Badge className="h-4 bg-primary text-[9px]">← Actual</Badge>}
                    </div>
                    <div className="mt-0.5 text-sm font-semibold">{step.action}</div>
                    <div className="mt-1 text-[11px] leading-relaxed opacity-75">{step.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RemindersTab({ lead }) {
  const qc = useQueryClient();
  const [dueAt, setDueAt] = useState("");
  const [note, setNote] = useState("");
  const { data } = useQuery({
    queryKey: ["zicasso-reminders", lead.id],
    queryFn: async () => (await api.get(`/zicasso/leads/${lead.id}/reminders`)).data,
  });
  const createMut = useMutation({
    mutationFn: () => api.post(`/zicasso/leads/${lead.id}/reminders`, { due_at: dueAt, note }),
    onSuccess: () => {
      toast.success("Recordatorio creado");
      setDueAt(""); setNote("");
      qc.invalidateQueries({ queryKey: ["zicasso-reminders", lead.id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo crear el recordatorio"),
  });
  const delMut = useMutation({
    mutationFn: (rid) => api.delete(`/zicasso/reminders/${rid}`),
    onSuccess: () => { toast.success("Recordatorio eliminado"); qc.invalidateQueries({ queryKey: ["zicasso-reminders", lead.id] }); },
  });
  const items = data?.items || [];
  const fmt = (iso) => { try { return format(new Date(iso), "d MMM yyyy · HH:mm", { locale: es }); } catch { return iso; } };
  return (
    <div className="space-y-4" data-testid="reminders-tab">
      <Card>
        <CardContent className="space-y-3 p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold"><Bell className="h-4 w-4 text-primary" /> Nuevo recordatorio</h3>
          <p className="text-[11px] text-muted-foreground">
            El aviso llegará in-app y por email a <strong>{lead.assigned_zc_agent_name || "el agente asignado"}</strong> en la fecha indicada.
          </p>
          <div>
            <Label className="text-xs">Fecha y hora</Label>
            <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} className="mt-1" data-testid="reminder-datetime-input" />
          </div>
          <div>
            <Label className="text-xs">Nota</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Ej: Llamar para confirmar fechas y presupuesto…" className="mt-1" data-testid="reminder-note-input" />
          </div>
          <Button size="sm" onClick={() => createMut.mutate()} disabled={createMut.isPending || !dueAt} data-testid="reminder-create-button">
            <Plus className="mr-1 h-3 w-3" /> {createMut.isPending ? "Creando…" : "Crear recordatorio"}
          </Button>
        </CardContent>
      </Card>
      <div className="space-y-2" data-testid="reminders-list">
        {items.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">Sin recordatorios para este lead.</p>
        ) : items.map((r) => (
          <div key={r.id} className="flex items-start justify-between gap-3 rounded-lg border p-3" data-testid={`reminder-${r.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                <Clock className="h-3.5 w-3.5 text-primary" /> {fmt(r.due_at)}
                {r.status === "sent"
                  ? <Badge variant="outline" className="h-4 text-[9px]">Enviado</Badge>
                  : <Badge className="h-4 bg-[#E2EBE5] text-[9px] text-[#244532] hover:bg-[#E2EBE5]">Pendiente</Badge>}
              </div>
              {r.note && <p className="mt-1 text-[12px] text-muted-foreground">{r.note}</p>}
            </div>
            <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-[#C45A45]" onClick={() => delMut.mutate(r.id)} data-testid={`reminder-delete-${r.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ZicassoLeadModal({ leadId, open, onClose, initialTab = "details" }) {
  const { data, isLoading } = useQuery({
    queryKey: ["zicasso-lead", leadId],
    queryFn: async () => (await api.get(`/zicasso/leads/${leadId}`)).data,
    enabled: !!leadId && open,
  });

  const lead = data;
  const stage = stageMeta(lead?.sales_stage);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] w-[95vw] max-w-6xl overflow-hidden p-0" data-testid="zicasso-lead-modal">
        <DialogTitle className="sr-only">Detalle del lead</DialogTitle>
        <DialogDescription className="sr-only">Ficha del lead con pestañas de detalles, mensajes, documentos, ventas, follow-up y buenas prácticas.</DialogDescription>
        {isLoading || !lead ? (
          <div className="space-y-3 p-6">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="flex h-full max-h-[92vh] flex-col">
            <div className="flex items-start justify-between border-b bg-secondary/30 p-5">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {initials(lead.traveler_name)}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate font-heading text-xl font-semibold" data-testid="modal-lead-name">{lead.traveler_name}</h2>
                    <span
                      className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                      style={{ background: stage.bg, color: stage.text }}
                    >
                      {stage.label}
                    </span>
                    {lead.is_overdue && (
                      <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> Atrasado</Badge>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    Ref: {lead.reference_id} · {(lead.countries || []).join(", ")} · asignado a{" "}
                    <span className="font-medium">{lead.assigned_zc_agent_name}</span>
                  </p>
                </div>
              </div>
            </div>

            <Tabs defaultValue={initialTab} key={`${leadId}-${initialTab}`} className="flex flex-1 flex-col overflow-hidden">
              <TabsList className="mx-5 mt-3 justify-start overflow-x-auto" data-testid="lead-tabs">
                <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                <TabsTrigger value="messages" data-testid="tab-messages">
                  <MessageSquare className="mr-1 h-3 w-3" /> Messages
                  {lead.messages_count > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">{lead.messages_count}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents">
                  <FileText className="mr-1 h-3 w-3" /> Documents
                </TabsTrigger>
                <TabsTrigger value="sales" data-testid="tab-sales">Sales</TabsTrigger>
                <TabsTrigger value="followup" data-testid="tab-followup">
                  <ListChecks className="mr-1 h-3 w-3" /> Follow-up
                  {lead.is_overdue && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-[#C45A45]" />}
                </TabsTrigger>
                <TabsTrigger value="reminders" data-testid="tab-reminders">
                  <Bell className="mr-1 h-3 w-3" /> Recordatorio
                </TabsTrigger>
                <TabsTrigger value="best-practices" data-testid="tab-best-practices">
                  <Lightbulb className="mr-1 h-3 w-3" /> Best Practices
                </TabsTrigger>
              </TabsList>

              <div className="thin-scroll flex-1 overflow-y-auto p-5">
                <TabsContent value="details" className="mt-0"><DetailsTab lead={lead} /></TabsContent>
                <TabsContent value="messages" className="mt-0"><MessagesTab lead={lead} /></TabsContent>
                <TabsContent value="documents" className="mt-0"><DocumentsTab lead={lead} /></TabsContent>
                <TabsContent value="sales" className="mt-0"><SalesTab lead={lead} /></TabsContent>
                <TabsContent value="followup" className="mt-0"><FollowUpTab lead={lead} /></TabsContent>
                <TabsContent value="reminders" className="mt-0"><RemindersTab lead={lead} /></TabsContent>
                <TabsContent value="best-practices" className="mt-0"><BestPracticesTab lead={lead} /></TabsContent>
              </div>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
